/**
 * WorkflowInspectionSection (Phase 5 rewrite)
 * 
 * Full inspection UI embedded in Workflow tab:
 * - Immediate checklist on start with timer auto-start
 * - InspectionTimerBar with book vs actual comparison
 * - Evidence tray + bulk capture
 * - Per-item evidence + AI suggestions (Catalyst+)
 * - No-template warning + manager link
 * - Completed read-only state with summary
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ChevronDown, ChevronRight, ClipboardCheck, Play, Save, CheckCircle2, User, Clock, AlertTriangle, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { ArtifactList } from '@/components/artifacts/ArtifactList';
import { ArtifactUploader } from '@/components/artifacts/ArtifactUploader';
import { ArtifactLinkCapture } from '@/components/artifacts/ArtifactLinkCapture';
import { LINK_ENTITY_TYPES, LINK_ROLES } from '@/lib/artifacts/constants';
import { InspectionTimerBar } from '@/components/inspection/InspectionTimerBar';
import { BulkCaptureButton } from '@/components/inspection/BulkCaptureButton';
import { EvidenceTray } from '@/components/inspection/EvidenceTray';
import { InspectionItemEvidence } from '@/components/inspection/InspectionItemEvidence';
import { useInspectionTimer } from '@/hooks/useInspectionTimer';
import { useInspectionEvidence } from '@/hooks/useInspectionEvidence';
import type { WorkflowInspection, WorkflowInspectionItem } from '@/hooks/useWorkflowReport';
import type { InspectionCapabilities } from '@/hooks/useInspectionCapabilities';
import { cn } from '@/lib/utils';

interface WorkflowInspectionSectionProps {
  inspections: WorkflowInspection[];
  canEdit: boolean;
  onStart: () => void;
  onSave: (inspectionId: string, items: { id: string; status: string; notes: string | null }[]) => void;
  onComplete: (inspectionId: string) => void;
  shopId?: string;
  ticketId?: string;
  capabilities?: InspectionCapabilities;
}

const STATUS_COLORS = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
}

export function WorkflowInspectionSection({
  inspections,
  canEdit,
  onStart,
  onSave,
  onComplete,
  shopId,
  ticketId,
  capabilities,
}: WorkflowInspectionSectionProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [localItems, setLocalItems] = useState<Record<string, WorkflowInspectionItem[]>>({});

  const latestInspection = inspections[0];
  const isActive = latestInspection && latestInspection.started_at && !latestInspection.completed_at;
  const hasNoItems = latestInspection && isActive && latestInspection.items.length === 0;

  // Timer hook
  const timer = useInspectionTimer(
    isActive ? latestInspection.id : null,
    ticketId ?? null,
    shopId ?? null
  );

  // Evidence hook
  const { unsorted, suggested, evidence: allEvidence } = useInspectionEvidence(
    latestInspection?.id ?? null
  );

  const getItems = (inspectionId: string, original: WorkflowInspectionItem[]) => {
    return localItems[inspectionId] || original;
  };

  const updateItem = (inspectionId: string, items: WorkflowInspectionItem[], itemId: string, field: 'status' | 'notes', value: string | null) => {
    const updated = items.map(i => i.id === itemId ? { ...i, [field]: value } : i);
    setLocalItems(prev => ({ ...prev, [inspectionId]: updated }));
  };

  // Get evidence for a specific item
  const getItemEvidence = (itemId: string) => allEvidence.filter(e => e.inspection_item_id === itemId);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <ClipboardCheck className="h-4 w-4 text-green-600" />
            <span className="font-semibold text-sm">Inspection</span>
            {isActive && <Badge className="bg-green-500/10 text-green-600 text-xs">Working</Badge>}
            {latestInspection?.completed_at && <Badge variant="secondary" className="text-xs">Completed</Badge>}
          </div>
          {/* Manager template link */}
          {capabilities?.canManageTemplates && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              onClick={e => { e.stopPropagation(); navigate('/inspection-templates'); }}
            >
              <Settings className="h-3 w-3 mr-1" />Templates
            </Button>
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pt-2 pb-4 space-y-3">

          {/* Timer bar (active inspection only) */}
          {isActive && latestInspection && (
            <InspectionTimerBar
              elapsedSeconds={timer.elapsedSeconds}
              timerStatus={timer.timerStatus}
              bookTimeMin={timer.bookTimeMin}
              isMutating={timer.isMutating}
              onPause={(detail) => timer.pauseTimer(latestInspection.id, detail)}
              onResume={() => timer.resumeTimer(latestInspection.id)}
              onStop={() => timer.stopTimer(latestInspection.id)}
            />
          )}

          {/* No-template warning */}
          {hasNoItems && (
            <div className="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-200">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>No active inspection template for this shop.</span>
              {capabilities?.canManageTemplates && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-yellow-800 dark:text-yellow-200 underline"
                  onClick={() => navigate('/inspection-templates')}
                >
                  Set up templates
                </Button>
              )}
            </div>
          )}

          {/* Evidence tray (unsorted + suggested) */}
          {isActive && shopId && latestInspection && (unsorted.length > 0 || suggested.length > 0) && (
            <EvidenceTray
              evidence={[...unsorted, ...suggested]}
              inspectionId={latestInspection.id}
              ticketId={ticketId || latestInspection.ticket_id}
              shopId={shopId}
              canEdit={canEdit}
            />
          )}

          {/* Bulk capture button */}
          {isActive && canEdit && shopId && latestInspection && (
            <BulkCaptureButton
              inspectionId={latestInspection.id}
              ticketId={ticketId || latestInspection.ticket_id}
              shopId={shopId}
            />
          )}

          {/* Session info header (completed state) */}
          {latestInspection && latestInspection.completed_at && (
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              {latestInspection.performer_name && (
                <span className="flex items-center gap-1"><User className="h-3 w-3" />Performed by: {latestInspection.performer_name}</span>
              )}
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Completed: {format(new Date(latestInspection.completed_at), 'MMM d, h:mm a')}</span>
              {latestInspection.duration_seconds != null && (
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Duration: {formatDuration(latestInspection.duration_seconds)}</span>
              )}
            </div>
          )}

          {/* Checklist items */}
          {latestInspection && latestInspection.items.length > 0 && (
            <div className="space-y-2">
              {getItems(latestInspection.id, latestInspection.items).map(item => (
                <div key={item.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{item.label}</span>
                    <div className="flex items-center gap-1">
                      {(['green', 'yellow', 'red'] as const).map(s => (
                        <button
                          key={s}
                          onClick={() => isActive && canEdit && updateItem(latestInspection.id, getItems(latestInspection.id, latestInspection.items), item.id, 'status', s)}
                          className={cn(
                            'w-6 h-6 rounded-full border-2 transition-all',
                            item.status === s ? `${STATUS_COLORS[s]} border-transparent scale-110` : 'border-muted-foreground/30 opacity-40',
                            isActive && canEdit && 'cursor-pointer hover:opacity-100'
                          )}
                          disabled={!isActive || !canEdit}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Per-item evidence thumbnails */}
                  <InspectionItemEvidence evidence={getItemEvidence(item.id)} />

                  {/* AI condition suggestion (Catalyst+) */}
                  {capabilities?.canAutoGrade && isActive && (() => {
                    const itemEv = getItemEvidence(item.id);
                    const graded = itemEv.find(e => e.suggested_condition);
                    if (!graded) return null;
                    return (
                      <Badge variant="outline" className="text-xs">
                        AI suggests: {graded.suggested_condition?.toUpperCase()} ({Math.round((graded.suggested_condition_confidence ?? 0) * 100)}%)
                      </Badge>
                    );
                  })()}

                  {/* AI note suggestion (Catalyst+) */}
                  {capabilities?.canAutoNotes && isActive && (() => {
                    const itemEv = getItemEvidence(item.id);
                    const noted = itemEv.find(e => e.suggested_note);
                    if (!noted) return null;
                    return (
                      <p className="text-xs text-muted-foreground italic">AI draft: {noted.suggested_note}</p>
                    );
                  })()}

                  {isActive && canEdit && (
                    <Textarea
                      value={item.notes || ''}
                      onChange={e => updateItem(latestInspection.id, getItems(latestInspection.id, latestInspection.items), item.id, 'notes', e.target.value || null)}
                      placeholder="Notes..."
                      rows={1}
                      className="text-xs"
                    />
                  )}
                  {!isActive && item.notes && (
                    <p className="text-xs text-muted-foreground">{item.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Inspection-level artifacts */}
          {shopId && latestInspection && (
            <>
              <ArtifactList
                shopId={shopId}
                entityType={LINK_ENTITY_TYPES.WORKFLOW_INSPECTION}
                entityId={latestInspection.id}
                canEdit={canEdit}
              />
              {canEdit && (
                <div className="flex items-center gap-1">
                  <ArtifactUploader
                    shopId={shopId}
                    entityType={LINK_ENTITY_TYPES.WORKFLOW_INSPECTION}
                    entityId={latestInspection.id}
                    role={LINK_ROLES.INSPECTION_EVIDENCE}
                  />
                  <ArtifactLinkCapture
                    shopId={shopId}
                    entityType={LINK_ENTITY_TYPES.WORKFLOW_INSPECTION}
                    entityId={latestInspection.id}
                    role={LINK_ROLES.INSPECTION_EVIDENCE}
                  />
                </div>
              )}
            </>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            {!latestInspection && canEdit && (
              <Button size="sm" onClick={onStart}>
                <Play className="h-3 w-3 mr-1" />Start Inspection
              </Button>
            )}
            {isActive && canEdit && (
              <>
                <Button size="sm" variant="outline" onClick={() => {
                  const items = getItems(latestInspection.id, latestInspection.items);
                  onSave(latestInspection.id, items.map(i => ({ id: i.id, status: i.status, notes: i.notes })));
                }}>
                  <Save className="h-3 w-3 mr-1" />Save
                </Button>
                <Button size="sm" onClick={() => {
                  const items = getItems(latestInspection.id, latestInspection.items);
                  onSave(latestInspection.id, items.map(i => ({ id: i.id, status: i.status, notes: i.notes })));
                  onComplete(latestInspection.id);
                }}>
                  <CheckCircle2 className="h-3 w-3 mr-1" />Mark Complete
                </Button>
              </>
            )}
            {latestInspection?.completed_at && canEdit && (
              <Button size="sm" variant="outline" onClick={onStart}>
                <Play className="h-3 w-3 mr-1" />New Inspection
              </Button>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
